<template>
  <div class="dc-lock-overlay">
    <div class="dc-lock-card">
      <div class="dc-lock-icon">
        <i class="fas fa-lock"></i>
      </div>

      <p class="dc-lock-title">หน้าจอถูกล็อก</p>
      <p class="dc-lock-user">{{ displayName }}</p>

      <!-- PIN is the primary method whenever the user has one: it is the fast
           path at a counter, and it is the credential whose leak costs least. -->
      <form v-if="method === 'pin'" @submit.prevent="submitPin">
        <label class="dc-lock-label" for="dc-lock-pin">PIN 6 หลัก</label>
        <input
          id="dc-lock-pin"
          ref="pinInput"
          v-model="pin"
          type="password"
          inputmode="numeric"
          maxlength="6"
          autocomplete="off"
          class="dc-lock-input dc-lock-input--pin"
          :disabled="busy"
          @input="onPinInput"
        />
        <p v-if="error" class="dc-lock-error">{{ error }}</p>
        <button
          type="submit"
          class="dc-lock-btn dc-lock-btn--primary"
          :disabled="busy || pin.length !== 6"
        >
          ปลดล็อก
        </button>
        <button type="button" class="dc-lock-link" @click="usePassword">
          ลืม PIN? ใช้รหัสผ่าน
        </button>
      </form>

      <form v-else @submit.prevent="submitPassword">
        <label class="dc-lock-label" for="dc-lock-password">รหัสผ่าน</label>
        <!-- autocomplete="new-password" so the browser does not offer a
             colleague's saved password into a shared counter machine. This
             reduces the exposure; it does not close it (spec 09 §4.3, §7). -->
        <input
          id="dc-lock-password"
          ref="passwordInput"
          v-model="password"
          type="password"
          autocomplete="new-password"
          class="dc-lock-input"
          :disabled="busy"
        />
        <p v-if="error" class="dc-lock-error">{{ error }}</p>
        <button
          type="submit"
          class="dc-lock-btn dc-lock-btn--primary"
          :disabled="busy || !password"
        >
          ปลดล็อก
        </button>
        <button
          v-if="hasPin && !pinDisabled"
          type="button"
          class="dc-lock-link"
          @click="usePin"
        >
          กลับไปใช้ PIN
        </button>
      </form>

      <!-- Without this, whoever takes over the machine is stuck at a
           colleague's lock screen, and the workaround is learning the
           colleague's password — the outcome the lock exists to prevent. -->
      <button
        type="button"
        class="dc-lock-link dc-lock-link--switch"
        :disabled="busy"
        @click="switchAccount"
      >
        เข้าใช้ด้วยบัญชีอื่น
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "LockOverlay",
  data() {
    return {
      method: "pin",
      pin: "",
      password: "",
      error: "",
      busy: false,
      pinDisabled: false,
    };
  },
  computed: {
    ...mapGetters("user", ["getUserInfo", "getHasPin"]),
    hasPin() {
      return this.getHasPin;
    },
    displayName() {
      const info = this.getUserInfo || {};
      const name = [info.firstNameTh, info.lastNameTh]
        .filter(Boolean)
        .join(" ")
        .trim();
      return name || info.email || "";
    },
  },
  created() {
    this.method = this.getHasPin ? "pin" : "password";
  },
  mounted() {
    this.focusActiveField();
  },
  methods: {
    focusActiveField() {
      this.$nextTick(() => {
        const el =
          this.method === "pin" ? this.$refs.pinInput : this.$refs.passwordInput;
        if (el && el.focus) el.focus();
      });
    },
    onPinInput() {
      // Digits only, so a stray character cannot make a 6-length string that
      // the server will reject anyway.
      this.pin = String(this.pin).replace(/\D/g, "").slice(0, 6);
      this.error = "";
    },
    usePin() {
      this.method = "pin";
      this.error = "";
      this.password = "";
      this.focusActiveField();
    },
    usePassword() {
      this.method = "password";
      this.error = "";
      this.pin = "";
      this.focusActiveField();
    },
    async submitPin() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.$store.dispatch("user/unlockWithPin", this.pin);
        this.finish();
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        this.pin = "";
        if (data.reason === "disabled") {
          this.pinDisabled = true;
          this.error = "PIN ถูกระงับชั่วคราว กรุณาปลดล็อกด้วยรหัสผ่าน";
          this.usePassword();
        } else if (data.reason === "no_pin") {
          this.error = "บัญชีนี้ยังไม่ได้ตั้ง PIN กรุณาใช้รหัสผ่าน";
          this.usePassword();
        } else {
          const left = data.remainingAttempts;
          this.error =
            typeof left === "number"
              ? `PIN ไม่ถูกต้อง เหลืออีก ${left} ครั้ง`
              : "PIN ไม่ถูกต้อง";
        }
      } finally {
        this.busy = false;
      }
    },
    async submitPassword() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.$store.dispatch("user/unlockWithPassword", this.password);
        // A password unlock re-enables a PIN the server had disabled.
        this.pinDisabled = false;
        this.finish();
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.password = "";
        this.error =
          status === 423
            ? "บัญชีถูกล็อกชั่วคราวจากการกรอกรหัสผ่านผิดหลายครั้ง"
            : "รหัสผ่านไม่ถูกต้อง";
      } finally {
        this.busy = false;
      }
    },
    finish() {
      this.pin = "";
      this.password = "";
      this.error = "";
      this.$emit("unlocked");
    },
    async switchAccount() {
      this.busy = true;
      try {
        await this.$store.dispatch("auth/userLogout");
      } catch (e) {
        // A failed logout call must not trap the next person on this screen.
      }
      this.$emit("switch-account");
    },
  },
};
</script>

<style scoped>
.dc-lock-overlay {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.92);
}

.dc-lock-card {
  width: 100%;
  max-width: 360px;
  padding: 32px 28px;
  border-radius: 12px;
  background: #fff;
  text-align: center;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.35);
}

.dc-lock-icon {
  font-size: 28px;
  color: #64748b;
  margin-bottom: 12px;
}

.dc-lock-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.dc-lock-user {
  margin: 4px 0 20px;
  font-size: 15px;
  color: #475569;
}

.dc-lock-label {
  display: block;
  text-align: left;
  font-size: 13px;
  color: #475569;
  margin-bottom: 6px;
}

.dc-lock-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 15px;
}

.dc-lock-input--pin {
  letter-spacing: 10px;
  text-align: center;
  font-size: 22px;
}

.dc-lock-error {
  margin: 10px 0 0;
  font-size: 13px;
  color: #dc2626;
}

.dc-lock-btn {
  width: 100%;
  margin-top: 16px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
}

.dc-lock-btn--primary {
  background: #2563eb;
  color: #fff;
}

.dc-lock-btn--primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.dc-lock-link {
  display: block;
  width: 100%;
  margin-top: 12px;
  background: none;
  border: none;
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
}

.dc-lock-link--switch {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
}
</style>
